import { holdInUtc, PushNotification } from "@repo/helpers";
import { KeyedArray, keyPair, RepFieldAudit } from "@repo/models";
import { useCallback } from "react";

import { useRepContext } from "../contexts/editableItemContext";
import * as Queries from "./queries";
import { useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useRepFieldAuditApi() {
	const { Get, Post } = useRepPortalApi();

	const saveFieldAudit = useCallback(
		function (repFieldAudit: RepFieldAudit, email: boolean) {
			const fieldAudit = {
				...repFieldAudit,
				questionGroups: undefined,
				responseMap: undefined,
			};

			return Post<number>("SaveFieldAudit", { email }, fieldAudit);
		},
		[Post],
	);

	const getFieldAuditQuestionOptions = useCallback(
		async function () {
			return Get<RepFieldAudit>("GetFieldAuditQuestionOptions").then(initialize);
		},
		[Get],
	);

	const getFieldAuditComplaintTypes = useCallback(
		async function () {
			return Get<keyPair[]>("GetFieldAuditComplaintTypes");
		},
		[Get],
	);

	const getLocations = useCallback(
		function (repId: number) {
			return Get<keyPair[]>("GetLocations", { repId });
		},
		[Get],
	);

	const getFormTypes = useCallback(
		async function (repId: number) {
			return Get<keyPair[]>("GetFormTypes", { repId });
		},
		[Get],
	);

	const initialize = useCallback(function (audit: RepFieldAudit) {
		if (!audit) return audit;
		audit.createdOn = holdInUtc(audit.createdOn);
		audit.auditDate = holdInUtc(audit.auditDate);
		audit.time = audit.time || null;
		return audit;
	}, []);

	const getRepFieldAudits = useCallback(
		async function (repId: number) {
			const fieldAudits = await Get<RepFieldAudit[]>("FieldAudits", { repId });
			return fieldAudits.map(initialize);
		},
		[Get, initialize],
	);

	const getFieldAuditQuestions = useCallback(
		async function (repId: number, entityId: number, formType: number) {
			return Get<RepFieldAudit>("GetFieldAuditQuestions", {
				repId,
				entityId,
				formType,
			}).then(initialize);
		},
		[Get, initialize],
	);

	const loadRepFieldAudits = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultArr();
				try {
					const audits = await getRepFieldAudits(repId);
					const keyedItems = new KeyedArray<RepFieldAudit>(audits, (b) => b.representativeFieldAuditId);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve field audits");
					return defaultArr();
				}
			};
		},
		[getRepFieldAudits],
	);

	return {
		getRepFieldAudits,
		getFieldAuditQuestions,
		saveFieldAudit,
		getFieldAuditQuestionOptions,
		getFieldAuditComplaintTypes,
		getLocations,
		getFormTypes,
		loadRepFieldAudits,
	};
}

export function useRepFieldAuditQueries() {
	const { repId } = useRepContext();
	const {
		loadRepFieldAudits,
		getFormTypes,
		getFieldAuditQuestionOptions,
		getFieldAuditComplaintTypes,
		getFieldAuditQuestions,
		getLocations,
	} = useRepFieldAuditApi();

	const useRepFieldAuditQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDITS, [repId, loadRepFieldAudits], loadRepFieldAudits(repId));

	const useFormTypesQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDIT_FORM_TYPES, [repId, getFormTypes], () => getFormTypes(repId));

	const useQuestionsQuery = (fieldAuditId: number, formTypeId: number) =>
		useQueryWrapper(
			Queries.REP_FIELD_AUDIT_QUESTIONS,
			[repId, fieldAuditId, formTypeId, getFieldAuditQuestions],
			() => getFieldAuditQuestions(repId, fieldAuditId, formTypeId),
		);

	const useQuestionOptionsQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDIT_QUESTION_OPTIONS, [repId, getFieldAuditQuestionOptions], () =>
			getFieldAuditQuestionOptions(),
		);

	const useComplaintTypesQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDIT_COMPLAINT_TYPES, [repId, getFieldAuditComplaintTypes], () =>
			getFieldAuditComplaintTypes(),
		);

	const useLocationsQuery = () =>
		useQueryWrapper(Queries.REP_FIELD_AUDIT_LOCATIONS, [repId, getLocations], () => getLocations(repId));

	return {
		useRepFieldAuditQuery,
		useFormTypesQuery,
		useQuestionsQuery,
		useQuestionOptionsQuery,
		useComplaintTypesQuery,
		useLocationsQuery,
	};
}

const defaultArr = () => new KeyedArray<RepFieldAudit>([], (b) => b.representativeFieldAuditId);
